// src/auth/Signup.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, Link as RouterLink, useLocation } from "react-router-dom";
import { API } from "../config/apiConfig";
import { portalGradientTextSx } from "../styles/dashboardBrand";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Link,
} from "@mui/material";

function Signup() {
  const location = useLocation();
  
  // Get referral code from URL query parameters
  const getRefCodeFromURL = () => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("ref") || "";
  };

  // Initialize form with referral code from URL if present
  const initialRefCode = getRefCodeFromURL();
  console.log("🚀 Component render - initialRefCode from URL:", initialRefCode);
  console.log("🚀 Component render - location.search:", location.search);
  
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    confirmEmail: "",
    password: "",
    confirmPassword: "",
    referralCode: initialRefCode,
  });
  
  console.log("🚀 Component render - form.referralCode:", form.referralCode);
  const [referrer, setReferrer] = useState(null);
  const [referrerName, setReferrerName] = useState("");
  const [checkingReferrer, setCheckingReferrer] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  // ✅ Validate referral code (can be called with optional code parameter)
  const validateReferralCode = async (codeToValidate = null) => {
    const code = codeToValidate || form.referralCode;
    if (!code) return;

    try {
      setCheckingReferrer(true);
      setReferrer(null);
      setReferrerName("");

      const url = API.validateReferral(code);
      console.log("🔍 Validating referral code:", code);
      console.log("🌐 API URL:", url);

      const res = await axios.get(url);
      console.log("✅ Referral API response:", res.data);

      if (res.data.success && res.data.referrer) {
        const ref = res.data.referrer;
        setReferrer(ref);
        const displayName =
          [ref.firstName, ref.lastName].filter(Boolean).join(" ") ||
          ref.userName ||
          ref.referralCode;

        setReferrerName(`Referred by: ${displayName}`);
      } else {
        setReferrerName("Invalid referral code");
        setReferrer(null);
      }
    } catch (err) {
      console.error("❌ Referral validation error:", err);
      console.error("❌ Error response:", err.response?.data);
      console.error("❌ Error status:", err.response?.status);
      
      // Show specific error message from server if available
      const errorMsg = err.response?.data?.message || "Could not verify referral code";
      setReferrerName(errorMsg);
      setReferrer(null);
    } finally {
      setCheckingReferrer(false);
    }
  };

  // Validate referral code on mount and when URL changes
  useEffect(() => {
    const refCode = getRefCodeFromURL();
    console.log("🔍 useEffect - Checking URL for ref parameter:", refCode);
    console.log("🔍 useEffect - location.search:", location.search);
    console.log("🔍 useEffect - Current form.referralCode:", form.referralCode);
    
    if (refCode) {
      console.log("✅ Found ref code in URL, updating form:", refCode);
      // Always update form to ensure it's set, even if it seems the same
      setForm((prev) => {
        console.log("📝 setForm called - prev.referralCode:", prev.referralCode, "new:", refCode);
        return { ...prev, referralCode: refCode };
      });
      // Validate the referral code after a brief delay to ensure state is updated
      setTimeout(() => {
        console.log("🔍 Validating referral code after state update:", refCode);
        validateReferralCode(refCode);
      }, 100);
    } else {
      console.log("⚠️ No ref code found in URL");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // ✅ Validate referral code on blur
  const handleReferralBlur = async () => {
    await validateReferralCode();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (form.email !== form.confirmEmail) {
      setError("Emails do not match");
      return;
    }

    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (!form.referralCode || !referrer) {
      setError("Please enter a valid referral code");
      return;
    }

    try {
      setLoading(true);

      // ✅ Payload matches backend expectations
      const payload = {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        password: form.password,
        confirmPassword: form.confirmPassword,
        referredByCode: form.referralCode,
      };

      console.log("Signup payload sent:", payload);

      const res = await axios.post(API.signup, payload, {
        headers: { "Content-Type": "application/json" },
      });

      if (res.data.success) {
        navigate("/verify-email", { state: { userId: res.data.userId } });
      } else {
        setError(res.data.error || "Signup failed");
      }
    } catch (err) {
      console.error("❌ Signup error:", err.response?.data || err.message);
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          "❌ Server error while signing up"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack
      sx={{
        minHeight: "100vh",
        alignItems: "center",
        justifyContent: "center",
        background: 'linear-gradient(135deg, #4FACFE 0%, #6366F1 40%, #9333EA 72%, #E11D48 100%)',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3) 0%, transparent 50%), radial-gradient(circle at 40% 40%, rgba(120, 219, 255, 0.2) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Paper 
        elevation={24} 
        sx={{ 
          p: 5, 
          width: "100%", 
          maxWidth: 450,
          borderRadius: 3,
          bgcolor: 'background.paper',
          backdropFilter: 'blur(20px)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Typography
          variant="h4"
          gutterBottom
          textAlign="center"
          sx={portalGradientTextSx({
            fontWeight: 700,
            mb: 2,
          })}
        >
          Create Account
        </Typography>
        <Typography 
          variant="h6" 
          textAlign="center" 
          color="text.secondary"
          sx={{ mb: 3 }}
        >
          Join Dreamy Apps Today
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              name="firstName"
              label="First Name"
              value={form.firstName}
              onChange={handleChange}
              required
              fullWidth
              disabled={loading}
            />
            <TextField
              name="lastName"
              label="Last Name"
              value={form.lastName}
              onChange={handleChange}
              required
              fullWidth
              disabled={loading}
            />
            <TextField
              name="email"
              label="Email"
              type="email"
              value={form.email}
              onChange={handleChange}
              required
              fullWidth
              disabled={loading}
            />
            <TextField
              name="confirmEmail"
              label="Verify Email"
              type="email"
              value={form.confirmEmail}
              onChange={handleChange}
              required
              fullWidth
              disabled={loading}
              error={form.confirmEmail.length > 0 && form.email !== form.confirmEmail}
              helperText={
                form.confirmEmail.length > 0 && form.email !== form.confirmEmail
                  ? "Emails do not match"
                  : ""
              }
            />
            <TextField
              name="password"
              label="Password"
              type="password"
              value={form.password}
              onChange={handleChange}
              required
              fullWidth
              disabled={loading}
            />
            <TextField
              name="confirmPassword"
              label="Confirm Password"
              type="password"
              value={form.confirmPassword}
              onChange={handleChange}
              required
              fullWidth
              disabled={loading}
            />
            <TextField
              name="referralCode"
              label="Referral Code"
              value={form.referralCode}
              onChange={handleChange}
              onBlur={handleReferralBlur}
              required
              fullWidth
              disabled={loading}
              helperText={
                checkingReferrer
                  ? "Checking referrer..."
                  : ""
              }
            />
            {referrerName && !checkingReferrer && (
              <div style={{ marginTop: '8px', fontSize: '14px' }}>
                <span
                  style={{
                    color: referrer ? '#16a34a' : '#dc2626',
                    fontWeight: referrer ? '600' : '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  {referrer ? '✅' : '❌'} {referrerName}
                </span>
              </div>
            )}

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Sign Up"
              )}
            </Button>

            <Typography variant="body2" textAlign="center">
              Already have an account?{" "}
              <Link component={RouterLink} to="/login" underline="hover">
                Login here
              </Link>
            </Typography>
          </Stack>
        </form>
      </Paper>
    </Stack>
  );
}

export default Signup;
